import React, { useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Animated,
  PanResponder,
  Platform,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { noteListLabel } from '../utils/studentNotesMarkdown';

const SWIPE_REVEAL = 72;
const SWIPE_THRESHOLD = 48;

export default function StudentNotesListItem({
  note,
  selected,
  onPress,
  onDeletePress,
  onMenuPress,
}) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const menuBtnRef = useRef(null);
  const translateX = useRef(new Animated.Value(0)).current;
  const openRef = useRef(false);

  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, g) =>
        Math.abs(g.dx) > 8 && Math.abs(g.dx) > Math.abs(g.dy),
      onPanResponderMove: (_, g) => {
        const x = Math.min(0, Math.max(-SWIPE_REVEAL, g.dx));
        translateX.setValue(x);
      },
      onPanResponderRelease: (_, g) => {
        if (g.dx < -SWIPE_THRESHOLD) {
          openRef.current = true;
          Animated.spring(translateX, {
            toValue: -SWIPE_REVEAL,
            useNativeDriver: true,
            friction: 8,
          }).start();
        } else {
          openRef.current = false;
          Animated.spring(translateX, {
            toValue: 0,
            useNativeDriver: true,
            friction: 8,
          }).start();
        }
      },
    })
  ).current;

  const closeSwipe = () => {
    openRef.current = false;
    Animated.spring(translateX, { toValue: 0, useNativeDriver: true }).start();
  };

  const handleDeleteTap = () => {
    closeSwipe();
    onDeletePress(note);
  };

  return (
    <View style={styles.rowWrap}>
      <View style={styles.deleteBehind}>
        <TouchableOpacity style={styles.deleteBtn} onPress={handleDeleteTap} activeOpacity={0.85}>
          <Text style={styles.deleteBtnText}>🗑</Text>
        </TouchableOpacity>
      </View>
      <Animated.View
        style={[styles.rowFront, { transform: [{ translateX }] }]}
        {...panResponder.panHandlers}
        {...(Platform.OS === 'web' ? { dataSet: { studentNotesRowFront: 'true' } } : {})}
      >
        <Pressable
          style={[styles.row, selected && styles.rowSelected]}
          onPress={() => {
            if (openRef.current) {
              closeSwipe();
              return;
            }
            onPress(note);
          }}
          {...(Platform.OS === 'web'
            ? {
                dataSet: {
                  studentNotesListRow: 'true',
                  ...(selected ? { studentNotesListSelected: 'true' } : {}),
                },
              }
            : {})}
        >
          <Text
            style={styles.label}
            numberOfLines={2}
            {...(Platform.OS === 'web' ? { dataSet: { studentNotesListLabel: 'true' } } : {})}
          >
            {noteListLabel(note)}
          </Text>
          <Pressable
            ref={menuBtnRef}
            style={styles.menuBtn}
            nativeID="student-notes-menu-btn"
            onPress={(e) => {
              e?.stopPropagation?.();
              const node = menuBtnRef.current;
              const scheduleOpen = (anchor) => {
                requestAnimationFrame(() => onMenuPress(note, anchor));
              };
              if (node && typeof node.measureInWindow === 'function') {
                node.measureInWindow((x, y, width, height) => {
                  scheduleOpen({ x, y, width, height });
                });
              } else {
                scheduleOpen(null);
              }
            }}
            hitSlop={12}
            {...(Platform.OS === 'web' ? { dataSet: { studentNotesMenuBtn: 'true' } } : {})}
          >
            <Text
              style={styles.menuDots}
              {...(Platform.OS === 'web' ? { dataSet: { studentNotesMenuDots: 'true' } } : {})}
            >
              ⋮
            </Text>
          </Pressable>
        </Pressable>
      </Animated.View>
    </View>
  );
}

function getStyles(theme) {
  const gold = theme.colors.primary || '#d4af37';
  return StyleSheet.create({
    rowWrap: {
      marginBottom: 4,
      overflow: 'hidden',
      borderRadius: 6,
    },
    deleteBehind: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: '#c62828',
      justifyContent: 'center',
      alignItems: 'flex-end',
      paddingRight: 20,
    },
    deleteBtn: {
      width: SWIPE_REVEAL,
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    deleteBtnText: { fontSize: 22 },
    rowFront: {
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 10,
      borderBottomWidth: 1,
      borderBottomColor: theme.isDarkMode ? '#333' : '#EEE',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    },
    rowSelected: {
      backgroundColor: theme.isDarkMode ? 'rgba(212, 175, 55, 0.15)' : 'rgba(212, 175, 55, 0.12)',
      borderLeftWidth: 3,
      borderLeftColor: gold,
    },
    label: {
      flex: 1,
      fontSize: 14,
      color: theme.colors.text,
      paddingRight: 8,
      backgroundColor: 'transparent',
    },
    menuBtn: {
      paddingHorizontal: 6,
      paddingVertical: 4,
      backgroundColor: 'transparent',
    },
    menuDots: {
      fontSize: 20,
      fontWeight: '700',
      color: theme.colors.text,
      ...(Platform.OS === 'web' ? { lineHeight: 20 } : {}),
    },
  });
}
